import React from 'react';
import TextInput from '../components/TextInput';
import WheelDatePicker from '../components/WheelDatePicker';
import ChoicePicker from '../components/ChoicePicker';
import JpAddressInput from '../components/JpAddressInput';
import '../components/JpAddressInput.css';
import { sendIcon } from '../chatIcons';
import { normalizeMyNumber } from '../GeneralProfile/useGeneralProfileFlow';
import type { useFamilyInfoFlow } from './useFamilyInfoFlow';
import type {
  FamilyMemberDraft,
  FamilyStep,
  Relationship,
  YesNo,
  MyNumberLink,
  DisabilityCategory,
} from './familyInfoTypes';
import {
  ALL_TAX_DEPENDENT_CATEGORY_KANJI,
  DEPENDENT_CHANGE_REASON_CODES,
  DEPENDENT_NON_RESIDENT_REASON_CODES,
  isDependentChangeReasonCode,
  suggestPrimaryTaxCategoryKanji,
  taxDependentCategoryLabel,
} from './familyInfoDependentEligibility';
import { isSpouseTaxFlow } from './familyInfoSpouseEligibility';
import {
  SPOUSE_CHANGE_REASON_CODES,
  isSpouseChangeReasonCode,
  type SpouseChangeReasonCode,
} from '../dependentDeduction/spouseSection/spouseChangeReason';

type Flow = ReturnType<typeof useFamilyInfoFlow>;

const YES_NO = (t: Flow['t']): { value: YesNo; label: string }[] => [
  { value: 'yes', label: t('submitDocuments.familyInfo.yes') },
  { value: 'no', label: t('submitDocuments.familyInfo.no') },
];

const INSURANCE_REQUIRED_OPTS = (t: Flow['t']): { value: YesNo; label: string }[] => [
  { value: 'yes', label: t('submitDocuments.familyInfo.required') },
  { value: 'no', label: t('submitDocuments.familyInfo.notRequired') },
];

function ComposerWithHelp({
  flow,
  step,
  children,
}: {
  flow: Flow;
  step: FamilyStep;
  children: React.ReactNode;
}) {
  const help = flow.helpTextForStep(step);
  return (
    <>
      {help ? (
        <div
          style={{
            padding: '10px 16px 0',
            fontSize: '0.9em',
            lineHeight: 1.55,
            color: 'var(--ff-text-muted, #555)',
          }}
        >
          {help}
        </div>
      ) : null}
      {children}
    </>
  );
}

export default function FamilyInfoChatComposer({ flow }: { flow: Flow }) {
  const { step, draft, setDraftField, t, wheelLocale, dob, setDob, taxYear } = flow;

  const submitDraft = (fromStep: FamilyStep, next: FamilyMemberDraft, label?: string) => {
    void flow.advanceFrom(fromStep, label, next);
  };

  const relOpts: { value: Relationship; label: string }[] = [
    { value: 'spouse', label: t('submitDocuments.familyInfo.relationship.spouse') },
    { value: 'child', label: t('submitDocuments.familyInfo.relationship.child') },
    { value: 'grandchild', label: t('submitDocuments.familyInfo.relationship.grandchild') },
    { value: 'father', label: t('submitDocuments.familyInfo.relationship.father') },
    { value: 'mother', label: t('submitDocuments.familyInfo.relationship.mother') },
  ];

  const myNumberOpts: { value: MyNumberLink; label: string }[] = [
    { value: 'linked', label: t('submitDocuments.familyInfo.myNumber.linked') },
    { value: 'not_linked', label: t('submitDocuments.familyInfo.myNumber.not_linked') },
  ];

  const categoryOpts = ALL_TAX_DEPENDENT_CATEGORY_KANJI.map((kanji) => ({
    value: kanji,
    label: taxDependentCategoryLabel(kanji, t),
  }));

  const nonResOpts = DEPENDENT_NON_RESIDENT_REASON_CODES.map((code) => ({
    value: code,
    label: t(`submitDocuments.dependentDeduction.dependents.nonResidentReason.option.${code}`),
  }));

  if (step === 'hasMembers' && flow.completedMembers.length === 0) {
    if (flow.hasMembersPromptRevealed < 1) return null;
    return (
      <div className="gen-profile-chat__composer-inner" style={{ padding: '12px 16px' }}>
        <ChoicePicker value={flow.hasMembersAnswer || null} options={YES_NO(t)} onChange={(v) => flow.handleHasMembers(v)} />
      </div>
    );
  }

  if (
    flow.expandedMemberIndex === null &&
    (step === 'addAnother' || (step === 'hasMembers' && flow.completedMembers.length > 0))
  ) {
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker value={flow.addAnother || null} options={YES_NO(t)} onChange={(v) => flow.handleAddAnother(v)} />
      </div>
    );
  }

  if (step === 'finalConfirm') {
    return null;
  }

  if (step === 'relationship') {
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={(draft.relationship || null) as Relationship | null}
          options={relOpts}
          onChange={(v) =>
            submitDraft('relationship', { ...draft, relationship: v }, relOpts.find((o) => o.value === v)?.label ?? v)
          }
        />
      </div>
    );
  }

  if (step === 'fullName') {
    return (
      <TextInput
        fields={[{ id: 'fullName', label: t('submitDocuments.familyInfo.summary.fullName'), required: true, validate: flow.validateFullName }]}
        values={{ fullName: draft.fullName }}
        onChange={(_, v) => setDraftField('fullName', v)}
        onSubmit={() => submitDraft('fullName', { ...draft, fullName: draft.fullName.trim() })}
        submitDisabled={!draft.fullName.trim()}
      />
    );
  }

  if (step === 'fullNameKana') {
    return (
      <TextInput
        fields={[{ id: 'fullNameKana', label: t('submitDocuments.familyInfo.summary.fullNameKana'), required: true, validate: flow.validateKana }]}
        values={{ fullNameKana: draft.fullNameKana }}
        onChange={(_, v) => setDraftField('fullNameKana', v)}
        onSubmit={() => submitDraft('fullNameKana', { ...draft, fullNameKana: draft.fullNameKana.trim() })}
        submitDisabled={!draft.fullNameKana.trim()}
      />
    );
  }

  if (step === 'dob') {
    const handleSendDob = () => {
      const built = flow.buildDobDraft();
      submitDraft('dob', built, built.dateOfBirth);
    };
    return (
      <div className="ff-text-input">
        <div className="ff-text-input__fields">
          <WheelDatePicker value={dob} onChange={setDob} locale={wheelLocale} />
        </div>
        <div className="ff-text-input__footer">
          <button type="button" className="ff-text-input__ok" onClick={handleSendDob}>
            {t('submitDocuments.chat.ok')}
          </button>
          <button type="button" className="ff-text-input__send" onClick={handleSendDob} aria-label={t('submitDocuments.profileName.send')}>
            <img src={sendIcon} alt="" aria-hidden />
          </button>
        </div>
      </div>
    );
  }

  const yesNoStep = (s: FamilyStep, field: keyof FamilyMemberDraft) => (
    <ComposerWithHelp flow={flow} step={s}>
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={(draft[field] as YesNo) || null}
          options={YES_NO(t)}
          onChange={(v) =>
            submitDraft(
              s,
              { ...draft, [field]: v },
              v === 'yes' ? t('submitDocuments.familyInfo.yes') : t('submitDocuments.familyInfo.no')
            )
          }
        />
      </div>
    </ComposerWithHelp>
  );

  if (step === 'sameHousehold') return yesNoStep('sameHousehold', 'livingInSameHousehold');
  if (step === 'outsideJapan') {
    return (
      <ComposerWithHelp flow={flow} step="outsideJapan">
        <div style={{ padding: '12px 16px' }}>
          <ChoicePicker
            value={draft.outsideJapan || null}
            options={YES_NO(t)}
            onChange={(v) => void flow.handleOutsideJapan(v)}
          />
        </div>
      </ComposerWithHelp>
    );
  }
  if (step === 'taxGate') return yesNoStep('taxGate', 'claimTaxDependent');
  if (step === 'sharedLivelihood') return yesNoStep('sharedLivelihood', 'sharedLivelihood');
  if (step === 'spouseFamilyWorker') return yesNoStep('spouseFamilyWorker', 'spouseFamilyWorker');
  if (step === 'changeDuringYear') return yesNoStep('changeDuringYear', 'changeDuringYear');
  if (step === 'insuranceGate') return yesNoStep('insuranceGate', 'registerHealthInsurance');
  if (step === 'insuranceRequired') {
    return (
      <ComposerWithHelp flow={flow} step="insuranceRequired">
        <div style={{ padding: '12px 16px' }}>
          <ChoicePicker
            value={draft.insuranceRequired || null}
            options={INSURANCE_REQUIRED_OPTS(t)}
            onChange={(v) => {
              const next: FamilyMemberDraft = {
                ...draft,
                insuranceRequired: v,
                registerHealthInsurance: v,
                healthInsuranceDependent: v,
                ...(v === 'no' ? { myNumberLinkStatus: '' as const, supportedDependent: '' as const } : {}),
              };
              submitDraft(
                'insuranceRequired',
                next,
                v === 'yes' ? t('submitDocuments.familyInfo.required') : t('submitDocuments.familyInfo.notRequired')
              );
            }}
          />
        </div>
      </ComposerWithHelp>
    );
  }
  if (step === 'supportedDependent') return yesNoStep('supportedDependent', 'supportedDependent');
  if (step === 'otherIncomeGate') return yesNoStep('otherIncomeGate', 'claimedByOtherIncome');
  if (step === 'taxNonResident') return yesNoStep('taxNonResident', 'taxNonResident');

  if (step === 'myNumber') {
    return (
      <ComposerWithHelp flow={flow} step="myNumber">
        <TextInput
          fields={[
            {
              id: 'myNumber',
              label: t('submitDocuments.profileName.field.myNumber'),
              type: 'tel',
              maxLength: 12,
              required: true,
              validate: flow.validateMyNumber,
            },
          ]}
          values={{ myNumber: draft.myNumber }}
          onChange={(_, v) => setDraftField('myNumber', normalizeMyNumber(v))}
          onSubmit={() => submitDraft('myNumber', { ...draft, myNumber: normalizeMyNumber(draft.myNumber) })}
          submitDisabled={normalizeMyNumber(draft.myNumber).length !== 12}
        />
      </ComposerWithHelp>
    );
  }

  if (step === 'myNumberLink') {
    return (
      <ComposerWithHelp flow={flow} step="myNumberLink">
        <div style={{ padding: '12px 16px' }}>
          <ChoicePicker
            value={draft.myNumberLinkStatus || null}
            options={myNumberOpts}
            onChange={(v) =>
              submitDraft(
                'myNumberLink',
                { ...draft, myNumberLinkStatus: v },
                myNumberOpts.find((o) => o.value === v)?.label ?? v
              )
            }
          />
        </div>
      </ComposerWithHelp>
    );
  }

  if (step === 'ifNotAddress') {
    return (
      <div className="ff-text-input">
        <div className="ff-text-input__fields">
          <JpAddressInput value={flow.memberAddress} onChange={flow.setMemberAddress} />
        </div>
        <div className="ff-text-input__footer">
          <button
            type="button"
            className="ff-text-input__ok"
            onClick={() => void flow.handleSendIfNotAddress()}
            disabled={!flow.canSendIfNotAddress}
          >
            {t('submitDocuments.chat.ok')}
          </button>
          <button
            type="button"
            className="ff-text-input__send"
            onClick={() => void flow.handleSendIfNotAddress()}
            disabled={!flow.canSendIfNotAddress}
            aria-label={t('submitDocuments.profileName.send')}
          >
            <img src={sendIcon} alt="" aria-hidden />
          </button>
        </div>
      </div>
    );
  }

  if (step === 'otherEarnerName') {
    return (
      <TextInput
        fields={[{ id: 'n', label: t('submitDocuments.familyInfo.otherEarnerName'), required: true }]}
        values={{ n: draft.otherEarnerName }}
        onChange={(_, v) => setDraftField('otherEarnerName', v)}
        onSubmit={() => submitDraft('otherEarnerName', { ...draft, otherEarnerName: draft.otherEarnerName.trim() })}
        submitDisabled={!draft.otherEarnerName.trim()}
      />
    );
  }

  if (step === 'otherEarnerAddress') {
    return (
      <TextInput
        fields={[{ id: 'a', label: t('submitDocuments.familyInfo.otherEarnerAddress'), required: true }]}
        values={{ a: draft.otherEarnerAddress }}
        onChange={(_, v) => setDraftField('otherEarnerAddress', v)}
        onSubmit={() => submitDraft('otherEarnerAddress', { ...draft, otherEarnerAddress: draft.otherEarnerAddress.trim() })}
        submitDisabled={!draft.otherEarnerAddress.trim()}
      />
    );
  }

  if (step === 'dependentIneligibleNotice') {
    const reasonKey = draft.taxIneligibilityReason
      ? `submitDocuments.dependentDeduction.dependents.ineligibleReason.${draft.taxIneligibilityReason}`
      : 'submitDocuments.dependentDeduction.dependents.ineligibleIntro';
    return (
      <ComposerWithHelp flow={flow} step="dependentIneligibleNotice">
        <div style={{ padding: '12px 16px' }}>
          <p style={{ margin: '0 0 12px', lineHeight: 1.55 }}>{t(reasonKey)}</p>
          <button
            type="button"
            className="ff-text-input__ok"
            onClick={() =>
              submitDraft('dependentIneligibleNotice', { ...draft }, t('submitDocuments.chat.ok'))
            }
          >
            {t('submitDocuments.chat.ok')}
          </button>
        </div>
      </ComposerWithHelp>
    );
  }

  if (step === 'taxDependentCategory') {
    const suggested = suggestPrimaryTaxCategoryKanji(draft, taxYear);
    const selected = draft.taxDependentCategory || suggested;
    return (
      <ComposerWithHelp flow={flow} step="taxDependentCategory">
        <div style={{ padding: '12px 16px' }}>
          {suggested && (
            <p style={{ fontSize: 12, color: '#555', margin: '0 0 8px' }}>
              {t('submitDocuments.familyInfo.suggestedCategory')}: {taxDependentCategoryLabel(suggested, t)}
            </p>
          )}
          <ChoicePicker
            value={selected || null}
            options={categoryOpts}
            onChange={(v) =>
              submitDraft(
                'taxDependentCategory',
                { ...draft, taxDependentCategory: v },
                taxDependentCategoryLabel(v, t)
              )
            }
          />
        </div>
      </ComposerWithHelp>
    );
  }

  if (step === 'estimatedIncome') {
    const incomeLabel = isSpouseTaxFlow(draft)
      ? t('submitDocuments.dependentDeduction.spouse.steps.spouseEstimatedIncome')
      : t('submitDocuments.familyInfo.estimatedIncome');
    return (
      <ComposerWithHelp flow={flow} step="estimatedIncome">
        <TextInput
          fields={[{ id: 'inc', label: incomeLabel, required: true }]}
          values={{ inc: draft.estimatedIncome }}
          onChange={(_, v) => setDraftField('estimatedIncome', v)}
          onSubmit={() => submitDraft('estimatedIncome', { ...draft, estimatedIncome: draft.estimatedIncome.trim() })}
          submitDisabled={!draft.estimatedIncome.trim()}
        />
      </ComposerWithHelp>
    );
  }

  if (step === 'disabilityStatus') {
    const disabilityOpts: { value: DisabilityCategory; label: string }[] = [
      { value: 'none', label: t('submitDocuments.profileName.disabilityCategory.option.none') },
      { value: 'general', label: t('submitDocuments.profileName.disabilityCategory.option.general') },
      { value: 'special', label: t('submitDocuments.profileName.disabilityCategory.option.special') },
    ];
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={draft.disabilityCategory || null}
          options={disabilityOpts}
          onChange={(v) =>
            submitDraft(
              'disabilityStatus',
              {
                ...draft,
                disabilityCategory: v,
                disabilityFacts: v === 'general' || v === 'special' ? draft.disabilityFacts : '',
              },
              disabilityOpts.find((o) => o.value === v)?.label ?? v
            )
          }
        />
      </div>
    );
  }

  if (step === 'disabilityFacts') {
    return (
      <ComposerWithHelp flow={flow} step="disabilityFacts">
        <TextInput
          fields={[
            {
              id: 'disabilityFacts',
              label: t('submitDocuments.profileName.disabilityFactsLabel'),
              required: true,
            },
          ]}
          values={{ disabilityFacts: draft.disabilityFacts }}
          onChange={(_id, value) => setDraftField('disabilityFacts', value)}
          onSubmit={() =>
            submitDraft('disabilityFacts', { ...draft, disabilityFacts: draft.disabilityFacts.trim() })
          }
          submitDisabled={!draft.disabilityFacts.trim()}
        />
      </ComposerWithHelp>
    );
  }

  if (step === 'changeDate') {
    const handleSendChangeDate = () => {
      const built = flow.buildTaxChangeDateDraft();
      submitDraft('changeDate', built, built.taxChangeDate);
    };
    return (
      <div className="ff-text-input">
        <div className="ff-text-input__fields">
          <WheelDatePicker value={flow.taxChangeDateWheel} onChange={flow.setTaxChangeDateWheel} locale={wheelLocale} />
        </div>
        <div className="ff-text-input__footer">
          <button type="button" className="ff-text-input__ok" onClick={handleSendChangeDate}>
            {t('submitDocuments.chat.ok')}
          </button>
          <button type="button" className="ff-text-input__send" onClick={handleSendChangeDate} aria-label={t('submitDocuments.profileName.send')}>
            <img src={sendIcon} alt="" aria-hidden />
          </button>
        </div>
      </div>
    );
  }

  if (step === 'changeReason') {
    if (isSpouseTaxFlow(draft)) {
      const changeOpts: { value: SpouseChangeReasonCode; label: string }[] = SPOUSE_CHANGE_REASON_CODES.map((code) => ({
        value: code,
        label: t(`submitDocuments.dependentDeduction.spouse.changeReason.option.${code}`),
      }));
      const selected = isSpouseChangeReasonCode(draft.taxChangeReason) ? draft.taxChangeReason : null;
      return (
        <div style={{ padding: '12px 16px' }}>
          <ChoicePicker
            value={selected}
            options={changeOpts}
            onChange={(v) =>
              submitDraft(
                'changeReason',
                { ...draft, taxChangeReason: v },
                changeOpts.find((o) => o.value === v)?.label ?? v
              )
            }
          />
        </div>
      );
    }
    const changeOpts = DEPENDENT_CHANGE_REASON_CODES.map((code) => ({
      value: code,
      label: t(`submitDocuments.dependentDeduction.dependents.changeReason.option.${code}`),
    }));
    const selected = isDependentChangeReasonCode(draft.taxChangeReason) ? draft.taxChangeReason : null;
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={selected}
          options={changeOpts}
          onChange={(v) =>
            submitDraft(
              'changeReason',
              { ...draft, taxChangeReason: v },
              changeOpts.find((o) => o.value === v)?.label ?? v
            )
          }
        />
      </div>
    );
  }

  if (step === 'changeReasonOther') {
    const changeReasonOtherLabel = isSpouseTaxFlow(draft)
      ? t('submitDocuments.dependentDeduction.spouse.steps.changeReasonOther')
      : t('submitDocuments.dependentDeduction.dependents.steps.changeReasonOther');
    return (
      <TextInput
        fields={[
          {
            id: 'reason',
            label: changeReasonOtherLabel,
            required: true,
          },
        ]}
        values={{ reason: draft.taxChangeReason === 'other' ? '' : draft.taxChangeReason }}
        onChange={(_, v) => setDraftField('taxChangeReason', v)}
        onSubmit={() => submitDraft('changeReasonOther', { ...draft, taxChangeReason: draft.taxChangeReason.trim() })}
        submitDisabled={!draft.taxChangeReason.trim() || draft.taxChangeReason === 'other'}
      />
    );
  }

  if (step === 'nonResidentReason') {
    return (
      <ComposerWithHelp flow={flow} step="nonResidentReason">
        <div style={{ padding: '12px 16px' }}>
          <ChoicePicker
            value={draft.nonResidentReason || null}
            options={nonResOpts}
            onChange={(v) => {
              const next =
                v === 'none'
                  ? { ...draft, nonResidentReason: v }
                  : { ...draft, nonResidentReason: v, remittanceAmount: v === 'remittance_380k' ? draft.remittanceAmount : '' };
              submitDraft(
                'nonResidentReason',
                next,
                nonResOpts.find((o) => o.value === v)?.label ?? v
              );
            }}
          />
        </div>
      </ComposerWithHelp>
    );
  }

  if (step === 'nonResidentReasonOther') {
    return (
      <ComposerWithHelp flow={flow} step="nonResidentReasonOther">
        <TextInput
          fields={[
            {
              id: 'reason',
              label: t('submitDocuments.familyInfo.steps.nonResidentReasonOther'),
              required: true,
            },
          ]}
          values={{ reason: draft.nonResidentReason === 'none' ? '' : draft.nonResidentReason }}
          onChange={(_, v) => setDraftField('nonResidentReason', v)}
          onSubmit={() =>
            submitDraft('nonResidentReasonOther', {
              ...draft,
              nonResidentReason: draft.nonResidentReason.trim(),
            })
          }
          submitDisabled={!draft.nonResidentReason.trim() || draft.nonResidentReason === 'none'}
        />
      </ComposerWithHelp>
    );
  }

  if (step === 'remittanceAmount') {
    return (
      <ComposerWithHelp flow={flow} step="remittanceAmount">
        <TextInput
          fields={[{ id: 'r', label: t('submitDocuments.familyInfo.remittanceAmount'), required: true }]}
          values={{ r: draft.remittanceAmount }}
          onChange={(_, v) => setDraftField('remittanceAmount', v)}
          onSubmit={() => submitDraft('remittanceAmount', { ...draft, remittanceAmount: draft.remittanceAmount.trim() })}
          submitDisabled={!draft.remittanceAmount.trim()}
        />
      </ComposerWithHelp>
    );
  }


  return null;
}
